import type { RegisterAttributionDto } from "../types/register.types";

const STORAGE_KEY = "coloca_signup_attribution";
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

const URL_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
  "ttclid",
  "li_fat_id",
  "msclkid",
] as const;

// Mirrors the MaxLength of each field in nest's RegisterAttributionPartDto: an
// over-long value would 400 the whole signup, so it is truncated here instead.
const MAX_LENGTH: Record<keyof RegisterAttributionDto, number> = {
  utm_source: 255,
  utm_medium: 255,
  utm_campaign: 255,
  utm_term: 255,
  utm_content: 255,
  gclid: 500,
  fbclid: 500,
  ttclid: 500,
  li_fat_id: 500,
  msclkid: 500,
  referrer: 1000,
  landing_page: 2000,
  first_seen_at: 64,
};

const isExternalReferrer = (referrer: string): boolean => {
  try {
    return !!referrer && new URL(referrer).origin !== window.location.origin;
  } catch {
    return false;
  }
};

// Last non-direct touch: a visit carrying campaign params replaces whatever was
// stored; a plain visit (bookmark, typed URL, in-app navigation) keeps it.
export function captureSignupAttribution(): void {
  try {
    const search = new URLSearchParams(window.location.search);
    const captured: RegisterAttributionDto = {};
    for (const key of URL_PARAMS) {
      const value = search.get(key)?.trim();
      if (value) captured[key] = value.slice(0, MAX_LENGTH[key]);
    }
    const referrer = document.referrer;
    const external = isExternalReferrer(referrer);
    const hasParams = Object.keys(captured).length > 0;
    if (!hasParams && (!external || readSignupAttribution())) return;

    if (external) captured.referrer = referrer.slice(0, MAX_LENGTH.referrer);
    captured.landing_page = window.location.href.slice(0, MAX_LENGTH.landing_page);
    captured.first_seen_at = new Date().toISOString();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(captured));
  } catch {
    // Storage unavailable (private mode, blocked cookies): attribution is best-effort.
  }
}

export function readSignupAttribution(): RegisterAttributionDto | undefined {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as RegisterAttributionDto;
    const seenAt = parsed.first_seen_at ? Date.parse(parsed.first_seen_at) : NaN;
    if (Number.isNaN(seenAt) || Date.now() - seenAt > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return undefined;
    }
    const clean: RegisterAttributionDto = {};
    for (const key of Object.keys(MAX_LENGTH) as (keyof RegisterAttributionDto)[]) {
      const value = parsed[key];
      if (typeof value === "string" && value) clean[key] = value.slice(0, MAX_LENGTH[key]);
    }
    return clean;
  } catch {
    return undefined;
  }
}

export function clearSignupAttribution(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Same best-effort contract as capture.
  }
}
